import apiClient from './apiClient';
import axios from 'axios'; // Only used for axios.isAxiosError type checking
import { config } from '../config';
import { authService } from './auth';
import { SegmentsJSON } from '../../backend/src/types/database';

const API_URL = config.apiUrl;

export interface SaveScriptData {
  script_name: string;
  script_description: string;
  selected_parts: string[];
  show_content: boolean;
  collapsed_parts: string[];
  voice: string;
  speech_language: string;
  speech_speed: number;
  comma_pause: number;
  period_pause: number;
  colon_pause: number;
  dash_pause: number;
  paragraph_pause: number;
  segmentation_mode: 'punctuation' | 'sentence' | 'paragraph';
  is_unguided?: boolean;
  zen_settings?: any; // Intelligent Zen Mode settings
}

export interface Voice {
  id: string;
  name: string;
  provider: string;
  voiceId: string;
  defaultPrompt: string;
  speechSpeed: number;
  sampleRate: number;
  outputFormat: string;
  sampleAudio: string;
  audioRef?: string | null; // Reference audio for Qwen3-TTS voice cloning (provider 'replicate-qwen3')
  isPrivate: boolean;
  userId?: string;
}

export interface ScriptPartData {
  part_id: string;
  part_order: number;
  part_name?: string;
  script_part_text: string;
  source_part_id?: string;
  custom_voice?: string;
  custom_language?: string;
  custom_speech_prompt?: string;
  custom_comma_pause?: number;
  custom_period_pause?: number;
  custom_colon_pause?: number;
  custom_dash_pause?: number;
  custom_paragraph_pause?: number;
  segments_data?: SegmentsJSON;
}

export interface ScriptSegmentData {
  segment_id: string;
  segment_text: string;
  segment_order: number;
  custom_voice?: string;
  custom_prompt?: string | null;
  audio_file?: string | null;
}

export interface Script {
  id: string;
  user_id: string;
  script_name: string;
  script_description?: string;
  is_draft: boolean;
  is_published: boolean;
  is_private: boolean;
  auto_loop?: boolean;
  is_anonymous?: boolean;
  selected_parts: string[];
  show_content: boolean;
  collapsed_parts: string[];
  voice: string;
  speech_language: string;
  speech_speed?: number;
  comma_pause: number;
  period_pause: number;
  colon_pause: number;
  dash_pause: number;
  paragraph_pause: number;
  segmentation_mode: 'punctuation' | 'sentence' | 'paragraph';
  art_file?: string;
  audio_file?: string; // Main TTS audio file (from finalization)
  final_audio_file?: string; // Final mixed audio file (includes background/music)
  audio_mix_data?: any; // Multi-track audio mix configuration (JSONB)
  zen_settings?: any; // Intelligent Zen Mode settings (JSONB)
  final_duration?: number; // Duration in seconds of final mixed audio
  is_unguided?: boolean; // True for instrumental-only meditations without speech
  art_prompt?: string; // Prompt used for AI art generation
  contributions?: any; // Credits for contributors (JSONB)
  thumbs_up: number;
  thumbs_down: number;
  listens: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
}

export interface ScriptPart {
  id: string;
  user_id: string;
  script_id: string;
  source_part_id?: string;
  part_id: string;
  part_order: number;
  part_name?: string;
  script_part_text: string;
  custom_voice?: string;
  custom_language?: string;
  custom_speech_prompt?: string;
  custom_comma_pause?: number;
  custom_period_pause?: number;
  custom_colon_pause?: number;
  custom_dash_pause?: number;
  custom_paragraph_pause?: number;
  is_private: boolean;
  is_published: boolean;
  usage_count: number;
  script_language: string;
  created_at: string;
  updated_at: string;
  segments?: ScriptSegment[]; // Legacy: Associated segments from old schema
  segments_data?: {
    segments: Array<{
      id: string;
      content: string;
      type: 'text' | 'pause';
      input_audio_file?: string;
      audio_file?: string;
      tts_cache_id?: string;
      generationStatus: 'idle' | 'queued' | 'generating' | 'completed' | 'error';
    }>;
    lastParsed?: string;
  }; // New JSON-based segment storage
}

export interface ScriptSegment {
  id: string;
  script_id: string;
  script_part_id: string;
  segment_id: string;
  segment_text: string;
  segment_order: number;
  custom_voice?: string;
  custom_prompt?: string;
  audio_file?: string;
  tts_cache_id?: string;
  ai_generated: boolean;
  created_at: string;
  updated_at: string;
}

export interface CompleteScript {
  script: Script;
  parts: ScriptPart[];
}

class ScriptService {
  private getAuthHeaders() {
    const token = authService.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Create a new script
   */
  async createScript(scriptData: SaveScriptData): Promise<Script> {
    try {
      const response = await apiClient.post(
        `${API_URL}/scripts`,
        scriptData,
        { headers: this.getAuthHeaders() }
      );
      return response.data.script;
    } catch (error) {
      console.error('Create script error:', error);
      throw error;
    }
  }

  /**
   * Update an existing script
   */
  async updateScript(scriptId: string, updates: Partial<SaveScriptData>): Promise<Script> {
    try {
      const response = await apiClient.put(
        `${API_URL}/scripts/${scriptId}`,
        updates,
        { headers: this.getAuthHeaders() }
      );
      return response.data.script;
    } catch (error) {
      console.error('Update script error:', error);
      throw error;
    }
  }

  /**
   * Get a complete script with all parts and segments
   */
  async getScript(scriptId: string): Promise<CompleteScript> {
    try {
      const response = await apiClient.get(
        `${API_URL}/scripts/${scriptId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Get script error:', error);
      throw error;
    }
  }

  /**
   * Get all scripts for the current user
   */
  async getUserScripts(): Promise<Script[]> {
    try {
      const response = await apiClient.get(
        `${API_URL}/scripts`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.scripts;
    } catch (error) {
      console.error('Get user scripts error:', error);
      throw error;
    }
  }

  /**
   * Get all enabled voices for the current user
   */
  async getUserVoices(): Promise<Voice[]> {
    try {
      const response = await apiClient.get(
        `${API_URL}/voices`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.voices;
    } catch (error) {
      console.error('Get user voices error:', error);
      throw error;
    }
  }

  /**
   * Delete a script
   */
  async deleteScript(scriptId: string): Promise<void> {
    try {
      await apiClient.delete(
        `${API_URL}/scripts/${scriptId}`,
        { headers: this.getAuthHeaders() }
      );
    } catch (error) {
      console.error('Delete script error:', error);
      throw error;
    }
  }

  /**
   * Create a script part
   */
  async createScriptPart(scriptId: string, partData: ScriptPartData): Promise<ScriptPart> {
    try {
      const response = await apiClient.post(
        `${API_URL}/scripts/${scriptId}/parts`,
        partData,
        { headers: this.getAuthHeaders() }
      );
      return response.data.part;
    } catch (error) {
      console.error('Create script part error:', error);
      throw error;
    }
  }

  /**
   * Update a script part
   */
  async updateScriptPart(scriptId: string, partId: string, updates: Partial<ScriptPartData>): Promise<ScriptPart> {
    try {
      const response = await apiClient.put(
        `${API_URL}/scripts/${scriptId}/parts/${partId}`,
        updates,
        { headers: this.getAuthHeaders() }
      );
      return response.data.part;
    } catch (error) {
      console.error('Update script part error:', error);
      throw error;
    }
  }

  /**
   * Reorder script parts atomically
   */
  async reorderScriptParts(scriptId: string, partOrders: { partId: string; order: number }[]): Promise<void> {
    try {
      await apiClient.put(
        `${API_URL}/scripts/${scriptId}/reorder-parts`,
        { partOrders },
        { headers: this.getAuthHeaders() }
      );
    } catch (error) {
      console.error('Reorder script parts error:', error);
      throw error;
    }
  }

  /**
   * Create a script segment
   */
  async createScriptSegment(scriptId: string, partId: string, segmentData: ScriptSegmentData): Promise<ScriptSegment> {
    try {
      const response = await apiClient.post(
        `${API_URL}/scripts/${scriptId}/parts/${partId}/segments`,
        segmentData,
        { headers: this.getAuthHeaders() }
      );
      return response.data.segment;
    } catch (error) {
      console.error('Create script segment error:', error);
      throw error;
    }
  }

  /**
   * Update a script segment
   */
  async updateScriptSegment(segmentId: string, updates: Partial<ScriptSegmentData>): Promise<ScriptSegment> {
    try {
      const response = await apiClient.put(
        `${API_URL}/segments/${segmentId}`,
        updates,
        { headers: this.getAuthHeaders() }
      );
      return response.data.segment;
    } catch (error) {
      console.error('Update script segment error:', error);
      throw error;
    }
  }

  /**
   * Save complete script with all parts and segments
   * This is a comprehensive save function that handles the entire script structure
   */
  async saveCompleteScript(
    scriptData: SaveScriptData,
    parts: Array<{
      partData: ScriptPartData; // Now includes segments_data
    }>,
    existingScriptId?: string
  ): Promise<CompleteScript> {
    try {
      let script: Script;
      
      if (existingScriptId) {
        try {
          // Check if the script exists first
          await this.getScript(existingScriptId);
          
          // If it exists, delete it and create a new one
          // This is simpler than trying to update parts/segments individually
          await this.deleteScript(existingScriptId);
          script = await this.createScript(scriptData);
        } catch (error: any) {
          // If script doesn't exist (404), just create a new one
          if (error.response?.status === 404) {
            console.log('Script not found, creating new script instead');
            script = await this.createScript(scriptData);
          } else {
            throw error;
          }
        }
      } else {
        // For new scripts: just create
        script = await this.createScript(scriptData);
      }

      // Step 2: Create script parts with segments_data
      const savedParts: ScriptPart[] = [];
      
      for (let i = 0; i < parts.length; i++) {
        const { partData } = parts[i];
        
        // Create the script part with segments_data included
        const partWithOrder = {
          ...partData,
          part_order: i + 1
        };
        
        const savedPart = await this.createScriptPart(script.id, partWithOrder);
        
        // segments_data is already saved as part of the part
        // No need to create individual segment records
        
        savedParts.push(savedPart);
      }

      return {
        script,
        parts: savedParts
      };
    } catch (error) {
      console.error('Save complete script error:', error);
      throw error;
    }
  }

  /**
   * Save script incrementally - updates existing script without deleting/recreating
   */
  async saveScriptIncrementally(
    scriptId: string,
    scriptData: SaveScriptData,
    partsToSave: Array<{
      partId: string; // meditation part ID (e.g., 'introduction')
      dbPartId?: string; // database part ID if updating existing
      partData: ScriptPartData; // Contains segments_data with all segments
    }>,
    deletedPartDbIds?: string[] // Database IDs of parts to delete
  ): Promise<CompleteScript> {
    try {
      // Step 1: Update script metadata
      const script = await this.updateScript(scriptId, scriptData);

      // Step 2: Delete parts that were removed
      if (deletedPartDbIds && deletedPartDbIds.length > 0) {
        console.log('Deleting parts from deletion queue:', deletedPartDbIds);
        await this.deleteParts(scriptId, deletedPartDbIds);
        console.log('Successfully deleted parts:', deletedPartDbIds);
      } else {
        console.log('No parts in deletion queue');
      }

      // Step 3: Separate existing and new parts
      const existingParts = partsToSave.filter(p => p.dbPartId);
      const newParts = partsToSave.filter(p => !p.dbPartId);

      // Step 4: Reorder existing parts to their final positions
      if (existingParts.length >= 1) {
        // Always use the reorder endpoint when we have existing parts
        // to avoid unique constraint violations during individual updates
        console.log('Using reorder endpoint for', existingParts.length, 'existing parts');

        const reorderData = existingParts.map(part => ({
          partId: part.dbPartId!,
          order: part.partData.part_order
        }));

        await this.reorderScriptParts(scriptId, reorderData);
      }

      // Step 5: Update existing parts (without part_order since we already reordered)
      const savedParts: ScriptPart[] = [];

      for (const partToSave of existingParts) {
        // Update existing part - exclude part_order from update since we already reordered
        const updateData = { ...partToSave.partData };
        delete (updateData as any).part_order;

        const savedPart = await this.updateScriptPart(
          scriptId,
          partToSave.dbPartId!,
          updateData
        );

        savedParts.push(savedPart);
      }

      // Step 6: Create new parts (now that existing parts are in their final positions)
      for (const partToSave of newParts) {
        const savedPart = await this.createScriptPart(scriptId, partToSave.partData);
        savedParts.push(savedPart);
      }

      // Sort saved parts by part_order to maintain correct order
      savedParts.sort((a, b) => (a.part_order || 0) - (b.part_order || 0));

      return {
        script,
        parts: savedParts
      };
    } catch (error) {
      console.error('Save script incrementally error:', error);
      throw error;
    }
  }

  /**
   * Delete a single segment
   */
  async deleteSegment(segmentId: string): Promise<void> {
    try {
      await apiClient.delete(
        `${API_URL}/segments/${segmentId}`,
        { headers: this.getAuthHeaders() }
      );
    } catch (error) {
      console.error('Delete segment error:', error);
      throw error;
    }
  }

  /**
   * Delete segments that are no longer needed
   */
  async deleteSegments(segmentIds: string[]): Promise<void> {
    try {
      const deletePromises = segmentIds.map(id => 
        apiClient.delete(
          `${API_URL}/segments/${id}`,
          { headers: this.getAuthHeaders() }
        )
      );
      
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Delete segments error:', error);
      throw error;
    }
  }

  /**
   * Delete parts that are no longer needed
   */
  async deleteParts(scriptId: string, partIds: string[]): Promise<void> {
    try {
      const deletePromises = partIds.map(id =>
        apiClient.delete(
          `${API_URL}/scripts/${scriptId}/parts/${id}`,
          { headers: this.getAuthHeaders() }
        )
      );
      
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Delete parts error:', error);
      throw error;
    }
  }

  /**
   * Finalize script audio by combining all segments into a single MP3
   */
  async finalizeScriptAudio(scriptId: string, scriptName: string, segments: Array<{
    id: string;
    type: 'text' | 'pause';
    content: string;
    audioFile?: string | null;
    pauseTime?: string;
    order: number;
  }>): Promise<{
    success: boolean;
    audioFile: string;
    duration: number;
    error?: string;
  }> {
    try {
      const response = await apiClient.post(
        `${API_URL}/finalize-script-audio`,
        {
          scriptId,
          scriptName,
          segments
        },
        {
          headers: this.getAuthHeaders(),
          // ffmpeg concatenation of long meditations (many segments + long silence
          // files) can far exceed the default 30s apiClient timeout
          timeout: 10 * 60 * 1000
        }
      );
      return response.data;
    } catch (error) {
      console.error('Finalize script audio error:', error);
      if (axios.isAxiosError(error) && error.response) {
        return {
          success: false,
          audioFile: '',
          duration: 0,
          error: error.response.data.error || 'Failed to finalize script audio'
        };
      }
      throw error;
    }
  }

  /**
   * Save audio mix data for a script (now consolidated in scripts table)
   */
  async saveAudioMixData(scriptId: string, mixData: any): Promise<void> {
    try {
      const response = await apiClient.post(
        `${API_URL}/scripts/${scriptId}/audio-mix`,
        mixData,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Save audio mix data error:', error);
      throw error;
    }
  }

  /**
   * Load audio mix data for a script (now consolidated in scripts table)
   */
  async loadAudioMixData(scriptId: string): Promise<any | null> {
    try {
      const response = await apiClient.get(
        `${API_URL}/scripts/${scriptId}/audio-mix`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // No saved mix data found
        return null;
      }
      console.error('Load audio mix data error:', error);
      throw error;
    }
  }

  /**
   * Delete audio mix data for a script (now consolidated in scripts table)
   */
  async deleteAudioMixData(scriptId: string): Promise<void> {
    try {
      await apiClient.delete(
        `${API_URL}/scripts/${scriptId}/audio-mix`,
        { headers: this.getAuthHeaders() }
      );
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // Mix data doesn't exist, which is fine
        return;
      }
      console.error('Delete audio mix data error:', error);
      throw error;
    }
  }

  /**
   * Update script with audio finalization fields
   */
  async updateScriptAudioFields(scriptId: string, updates: {
    final_audio_file?: string;
    final_duration?: number;
    is_unguided?: boolean;
    art_prompt?: string;
    contributions?: any;
  }): Promise<Script> {
    try {
      const response = await apiClient.put(
        `${API_URL}/scripts/${scriptId}`,
        updates,
        { headers: this.getAuthHeaders() }
      );
      return response.data.script;
    } catch (error) {
      console.error('Update script audio fields error:', error);
      throw error;
    }
  }

  /**
   * Finalize audio mix by uploading audio file
   */


  /**
   * Finalize audio mix with upload progress tracking
   */
  async finalizeAudioMixWithProgress(
    scriptId: string, 
    audioBlob: Blob,
    onProgress?: (progress: number) => void
  ): Promise<{
    success: boolean;
    audioFile: string;
    duration: number;
    error?: string;
  }> {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'final_mix.webm');

      const response = await apiClient.post(
        `${API_URL}/scripts/${scriptId}/finalize-audio-mix`,
        formData,
        {
          headers: {
            ...this.getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              if (onProgress) {
                onProgress(progress);
              }
            }
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Finalize audio mix error:', error);
      if (axios.isAxiosError(error) && error.response) {
        return {
          success: false,
          audioFile: '',
          duration: 0,
          error: error.response.data.error || 'Failed to finalize audio mix'
        };
      }
      throw error;
    }
  }

  /**
   * Get tags for a script
   */
  async getScriptTags(scriptId: string): Promise<Array<{ id: string; script_id: string; tag_name: string; created_at: string }>> {
    try {
      const response = await apiClient.get(`${API_URL}/scripts/${scriptId}/tags`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      // Backend returns { success: true, tags: [...] }
      return response.data.tags || [];
    } catch (error) {
      console.error('Get script tags error:', error);
      throw error;
    }
  }

  // Search for existing script parts to reuse
  async searchScriptParts(params: {
    part_id?: string;
    scope?: 'my' | 'all';
    sort?: 'date_asc' | 'date_desc' | 'usage_asc' | 'usage_desc';
    keyword?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    parts: Array<{
      id: string;
      part_id: string;
      part_name: string;
      script_name: string;
      script_part_text: string;
      text_length: number;
      created_at: string;
      script_id: string;
      usage_count: number;
    }>;
    offset: number;
    limit: number;
  }> {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const queryParams = new URLSearchParams();
      if (params.part_id) queryParams.append('part_id', params.part_id);
      if (params.scope) queryParams.append('scope', params.scope);
      if (params.sort) queryParams.append('sort', params.sort);
      if (params.keyword) queryParams.append('keyword', params.keyword);
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());

      const response = await apiClient.get(`${API_URL}/script-parts/search?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Search script parts error:', error);
      throw error;
    }
  }

  // Update script part with source tracking
  async updateScriptPartWithSource(
    scriptId: string, 
    partId: string, 
    data: {
      part_name?: string;
      script_part_text?: string;
      source_part_id?: string;
      source_script_id?: string;
    }
  ): Promise<{ part: ScriptPart }> {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await apiClient.put(`${API_URL}/scripts/${scriptId}/parts/${partId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Update script part with source error:', error);
      throw error;
    }
  }
}

export const scriptService = new ScriptService(); 